import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff, ScreenShareOff, UserPlus, Users, Volume2, X } from 'lucide-react';
import { useI18n } from '../../lib/i18n';
import { browserName, fmtElapsed, secondsLeft } from '../../lib/share/peer';
import {
  anyBandwidthLimited, occupancy, perViewerBitrateKbps, rosterRank, rosterStateOf, uploadStressed, type RosterState,
} from '../../lib/share/mesh';
import type { PeerStats } from '../../lib/share/types';
import { noticeText, useShare } from './ShareProvider';

/**
 * La píldora del sharer, fija bajo la barra. En la malla multi-espectador (spec §14) muestra
 * "Sharing with N people · 04:12" con un botón "People" que abre el roster (invitado / conectando /
 * mirando / salió, con punto de calidad y "Remove" por fila) y un botón "Add people" para invitar a
 * más EN VIVO sin cortar a los demás. Con UN solo viewer se comporta como antes.
 *
 * El bitrate por viewer BAJA al crecer el número (presupuesto de subida repartido): es un límite
 * FÍSICO, así que cuando la subida se estresa la píldora lo DICE ("Quality reduced · N watching"),
 * no lo esconde.
 *
 * Audio: el sharer transmite su voz+sistema a TODOS; el Mute aplica a todos. La voz de cada viewer
 * suena en un <audio> oculto del provider; si el navegador exigió gesto sale "Tap to hear".
 */

/** Punto de calidad de tres colores a partir de los stats del peer de un viewer. */
function qdot(stats: PeerStats | undefined): string {
  return stats?.quality ?? 'good';
}

export function SharerPill() {
  const { t } = useI18n();
  const s = useShare();
  const [, tick] = useState(0);
  const [rosterOpen, setRosterOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const id = window.setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  // El roster se cierra con un clic afuera o Esc.
  useEffect(() => {
    if (!rosterOpen) return undefined;
    const onDown = (e: PointerEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setRosterOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setRosterOpen(false); };
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('pointerdown', onDown, true); window.removeEventListener('keydown', onKey); };
  }, [rosterOpen]);

  const b = s.broadcast;
  if (!b) return null;

  if (!s.mine) {
    return <div className="ss-pill other"><span>{t('share.otherTab')}</span></div>;
  }

  const participants = b.participants;
  const activeParts = participants.filter((p) => p.status === 'accepted' || p.status === 'live');
  const pendingParts = participants.filter((p) => p.status === 'pending');
  const liveParts = participants.filter((p) => p.status === 'live');
  const occ = occupancy(b);

  // ── Nadie aceptó todavía: "Ringing {name}…" con Cancel (igual que el 1:1) ──
  if (activeParts.length === 0 && pendingParts.length > 0) {
    const first = pendingParts[0]!;
    return (
      <div className="ss-pill ringing" role="status">
        <span className="ss-pill-dot" aria-hidden />
        <span>{t('share.ringing', { name: first.viewer.name, s: secondsLeft(first.expiresAt, Date.now()) })}</span>
        {pendingParts.length > 1 && <span className="ss-chip">{t('share.plusN', { n: pendingParts.length - 1 })}</span>}
        {s.notice && <span className="ss-pill-note">{noticeText(s.notice, t)}</span>}
        <button type="button" className="btn ghost sm" onClick={s.stopSharing}><X size={14} strokeWidth={2} /> {t('share.cancel')}</button>
      </div>
    );
  }

  // ── En vivo con uno o más viewers ──
  const since = s.connectedAt ?? (activeParts[0]?.acceptedAt ? Date.parse(activeParts[0]!.acceptedAt!) : Date.now());
  const anyLive = liveParts.length > 0;
  const connecting = !anyLive; // aceptaron pero ninguno conectó todavía
  const cap = s.capture;
  const n = activeParts.length;

  // Estrés de subida: reparto por viewer contra el presupuesto + algún peer limitado por banda + cap.
  const perViewer = perViewerBitrateKbps({ uploadBudgetKbps: b.uploadBudgetKbps, nPeers: n, ceilingKbps: b.maxBitrateKbps });
  const stressed = uploadStressed({
    nPeers: n, perViewerKbps: perViewer, max: occ.max,
    anyBandwidthLimited: anyBandwidthLimited(activeParts.map((p) => s.peerStats[p.viewer.id])),
  });
  const anyRelay = activeParts.some((p) => s.peerStats[p.viewer.id]?.relayUsed);

  const sysChip = cap?.systemAudio === 'included'
    ? { cls: 'sys', text: t('share.systemAudio') }
    : cap?.systemAudio === 'unavailable'
      ? { cls: 'muted', text: t('share.systemAudioNA', { browser: browserName(navigator.userAgent) ?? t('share.yourBrowser') }) }
      : cap?.systemAudio === 'notIncluded'
        ? { cls: 'muted', text: t('share.systemAudioFull') }
        : null;

  // Orden del roster: mirando < conectando < invitado < salió; los que salieron no se listan (ya no están).
  const roster = participants
    .map((p) => ({ p, st: rosterStateOf(p, s.peerPhases[p.viewer.id]) }))
    .filter((r) => r.st !== 'left')
    .sort((a, c) => rosterRank(a.st) - rosterRank(c.st) || a.p.viewer.name.localeCompare(c.p.viewer.name));

  const rosterLabel: Record<RosterState, string> = {
    invited: t('share.rInvited'), connecting: t('share.rConnecting'), watching: t('share.rWatching'), left: t('share.rLeft'),
  };

  return (
    <div className="ss-pill live" role="status" ref={wrapRef}>
      <span className="ss-pill-dot" aria-hidden />
      <span>{connecting ? t('share.connectingShort') : n === 1 ? t('share.sharingWith', { name: activeParts[0]!.viewer.name }) : t('share.sharingWithN', { n })}</span>
      {!connecting && <span className="ss-mono">{fmtElapsed(Date.now() - since)}</span>}

      {/* Roster: cuántos miran + popover con la lista y "Remove". */}
      <div className="ss-roster-wrap">
        <button type="button" className="btn ghost sm ss-roster-btn" onClick={() => setRosterOpen((o) => !o)}
          aria-expanded={rosterOpen} title={t('share.roster')}>
          <Users size={14} strokeWidth={2} /> <span>{t('share.nWatching', { n: liveParts.length })}</span>
        </button>
        {rosterOpen && (
          // Es una LISTA (nombre + estado + Remove), no un menú: sin role="menu" el lector de
          // pantalla no anuncia "menú" vacío ni exige navegación por flechas que no existe.
          <div className="ss-roster" role="list">
            <div className="ss-roster-head">{t('share.roster')}</div>
            {roster.map(({ p, st }) => (
              <div key={p.viewer.id} className="ss-roster-row" role="listitem">
                <span className={`ss-qdot ${st === 'watching' ? qdot(s.peerStats[p.viewer.id]) : 'idle'}`} aria-hidden />
                <span className="ss-roster-name">{p.viewer.name}</span>
                {p.viewerMicMuted && st === 'watching' && <MicOff size={11} strokeWidth={2} aria-hidden className="ss-roster-mic" />}
                <span className="ss-roster-state">{rosterLabel[st]}</span>
                <button type="button" className="btn ghost sm ss-roster-remove" onClick={() => s.removeViewer(p.viewer.id)}
                  title={t('share.remove')} aria-label={`${t('share.remove')} ${p.viewer.name}`}>
                  <X size={12} strokeWidth={2} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Agregar más EN VIVO: reusa la captura, invita a otros sin cortar a los demás. */}
      <button type="button" className="btn ghost sm ss-add-people" onClick={() => s.openPicker('add')} disabled={occ.full}
        title={occ.full ? t('share.broadcastFull', { max: occ.max }) : t('share.addPeople')}>
        <UserPlus size={14} strokeWidth={2} /> <span>{t('share.addPeople')}</span>
      </button>

      {/* Aviso de un "Add people" que falló (ocupado / no habilitado / lleno / error): sin esto el
          fallo era MUDO — el toast del provider solo sale sin broadcast, y la píldora en vivo no lo
          pintaba (igual que la rama "ringing" de arriba). */}
      {s.notice && <span className="ss-pill-note">{noticeText(s.notice, t)}</span>}

      {/* Aviso de subida estresada: el bitrate por viewer bajó — se dice, no se esconde. */}
      {stressed && anyLive && (
        <span className="ss-chip slow" title={t('share.qualityReducedHint')}>{t('share.qualityReduced')}</span>
      )}
      {anyRelay && <span className="ss-chip relay">{t('share.relayed')}</span>}
      {sysChip && <span className={`ss-chip ${sysChip.cls}`} title={sysChip.text}><Volume2 size={11} strokeWidth={2} aria-hidden /> {sysChip.text}</span>}

      {/* En 1:1 el mute del micrófono del viewer se muestra prominente en la píldora (como siempre); con
          varios viewers va por-viewer en el roster (arriba), que no cabe repetir en la píldora. */}
      {n === 1 && activeParts[0]?.viewerMicMuted && (
        <span className="ss-chip muted"><MicOff size={11} strokeWidth={2} aria-hidden /> {t('share.viewerMuted', { name: activeParts[0]!.viewer.name })}</span>
      )}

      {/* La voz de los viewers bloqueada por autoplay → botón para destrabar a todos. */}
      {!connecting && s.hearBlocked && (
        <button type="button" className="btn ghost sm ss-hear" onClick={s.hearViewer}><Volume2 size={14} strokeWidth={2} /> {t('share.hearAll')}</button>
      )}
      {!connecting && s.mixerSuspended && (
        <button type="button" className="btn ghost sm ss-hear ss-resume-audio" onClick={s.resumeAudio}><Volume2 size={14} strokeWidth={2} /> {t('share.letHearAll')}</button>
      )}

      {cap?.mic === 'denied' && <span className="ss-pill-note warn">{t('share.micDenied')}</span>}
      {cap?.mic === 'none' && <span className="ss-pill-note warn">{t('share.noMic')}</span>}
      {cap?.mic === 'on' && (
        <button type="button" className={`btn ghost sm${cap.muted ? ' is-muted' : ''}`} onClick={s.toggleMute} title={cap.muted ? t('share.unmuteMic') : t('share.mute')} aria-pressed={cap.muted}>
          {cap.muted ? <MicOff size={14} strokeWidth={2} /> : <Mic size={14} strokeWidth={2} />}
          {cap.muted ? t('share.unmuteMic') : t('share.mute')}
        </button>
      )}
      <button type="button" className="btn ghost sm ss-stop" onClick={s.stopSharing}><ScreenShareOff size={14} strokeWidth={2} /> {t('share.stop')}</button>
    </div>
  );
}
